import { useEffect, useMemo, useState } from 'react';
import { Image, Platform, ScrollView, Text, View } from 'react-native';
import { type Href, useLocalSearchParams, useRouter } from 'expo-router';
import type {
  OAuthAuthorizationDetails,
  OAuthRedirect,
} from '@supabase/supabase-js';
import { Button, LoadingSpinner, WebHead } from '../../components';
import { trackEvent } from '../../lib/analytics';
import { setPendingAuthRedirect } from '../../lib/auth-redirect';
import { sendClientObservability } from '../../lib/observability';
import { supabase } from '../../lib/supabase';

type ConsentStatus = 'loading' | 'ready' | 'redirecting' | 'error';
type ConsentDecision = 'approve' | 'deny';

const TOOL_ACCESS = [
  'Financial summary, account balances, holdings, net worth, per diem, and FI age',
  'Transaction search results and categorization details',
  'Budget-versus-actual spending by category',
  'FIRE plan analysis, projections, and simulation methodology',
];

const SCOPE_COPY: Record<string, string> = {
  openid: 'Confirm your Per Diem identity',
  email: 'Share your email address',
  profile: 'Share basic profile details',
  phone: 'Share your phone number',
};

function firstParam(value: string | string[] | undefined): string | null {
  if (Array.isArray(value)) return value[0] ?? null;
  return value ?? null;
}

function isOAuthRedirect(data: OAuthAuthorizationDetails | OAuthRedirect): data is OAuthRedirect {
  return 'redirect_url' in data;
}

function formatClientHost(uri: string | null | undefined): string | null {
  if (!uri) return null;

  try {
    return new URL(uri).host;
  } catch {
    return uri;
  }
}

function redirectToExternalUrl(url: string) {
  if (Platform.OS === 'web' && typeof window !== 'undefined') {
    window.location.assign(url);
  }
}

function getConsentEventMetadata(
  details: OAuthAuthorizationDetails | null,
  decision?: ConsentDecision,
) {
  const scopes = (details?.scope ?? '')
    .split(/\s+/)
    .map((scope) => scope.trim())
    .filter(Boolean);

  return {
    clientHost: formatClientHost(details?.client.uri) ?? 'unknown',
    redirectHost: formatClientHost(details?.redirect_uri) ?? 'unknown',
    scopeCount: scopes.length,
    ...(decision ? { decision } : {}),
  };
}

export default function OAuthConsentScreen() {
  const router = useRouter();
  const params = useLocalSearchParams<{ authorization_id?: string | string[] }>();
  const authorizationId = firstParam(params.authorization_id);
  const [status, setStatus] = useState<ConsentStatus>('loading');
  const [details, setDetails] = useState<OAuthAuthorizationDetails | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [decisionInFlight, setDecisionInFlight] = useState<ConsentDecision | null>(null);

  const requestedScopes = useMemo(
    () =>
      (details?.scope ?? '')
        .split(/\s+/)
        .map((scope) => scope.trim())
        .filter(Boolean),
    [details?.scope],
  );

  useEffect(() => {
    let cancelled = false;

    const loadAuthorization = async () => {
      if (Platform.OS !== 'web') {
        setStatus('error');
        setErrorMessage('Per Diem connector authorization must be completed in a web browser.');
        return;
      }

      if (!authorizationId) {
        setStatus('error');
        setErrorMessage('Missing assistant connection request.');
        return;
      }

      const returnPath = `/oauth/consent?authorization_id=${encodeURIComponent(authorizationId)}`;
      const { data: sessionData, error: sessionError } = await supabase.auth.getSession();
      if (cancelled) return;

      if (sessionError) {
        setStatus('error');
        setErrorMessage(sessionError.message);
        return;
      }

      if (!sessionData.session) {
        await setPendingAuthRedirect(returnPath);
        if (cancelled) return;
        router.replace(
          `/(auth)/login?redirect=${encodeURIComponent(returnPath)}` as Href,
        );
        return;
      }

      const { data, error } =
        await supabase.auth.oauth.getAuthorizationDetails(authorizationId);
      if (cancelled) return;

      if (error || !data) {
        setStatus('error');
        setErrorMessage(error?.message ?? 'Invalid assistant connection request.');
        return;
      }

      if (isOAuthRedirect(data)) {
        setStatus('redirecting');
        redirectToExternalUrl(data.redirect_url);
        return;
      }

      setDetails(data);
      setStatus('ready');
      trackEvent({
        eventName: 'mcp.oauth.request_loaded',
        route: '/oauth/consent',
        screen: 'oauth-consent',
        metadata: getConsentEventMetadata(data),
      });
    };

    void loadAuthorization();

    return () => {
      cancelled = true;
    };
  }, [authorizationId, router]);

  const completeDecision = async (decision: ConsentDecision) => {
    if (!authorizationId || decisionInFlight) return;

    setDecisionInFlight(decision);
    setErrorMessage(null);

    const { data, error } =
      decision === 'approve'
        ? await supabase.auth.oauth.approveAuthorization(authorizationId, {
            skipBrowserRedirect: true,
          })
        : await supabase.auth.oauth.denyAuthorization(authorizationId, {
            skipBrowserRedirect: true,
          });

    if (error || !data) {
      await sendClientObservability({
        kind: 'event',
        eventName: 'mcp.oauth.failed',
        route: '/oauth/consent',
        screen: 'oauth-consent',
        metadata: getConsentEventMetadata(details, decision),
      });
      setDecisionInFlight(null);
      setStatus('error');
      setErrorMessage(error?.message ?? 'Unable to complete assistant connection approval.');
      return;
    }

    await sendClientObservability({
      kind: 'event',
      eventName: decision === 'approve' ? 'mcp.oauth.approved' : 'mcp.oauth.denied',
      route: '/oauth/consent',
      screen: 'oauth-consent',
      metadata: getConsentEventMetadata(details, decision),
    });

    setStatus('redirecting');
    redirectToExternalUrl(data.redirect_url);
  };

  if (status === 'loading' || status === 'redirecting') {
    return (
      <>
        <WebHead><title>Authorize Per Diem Connector</title></WebHead>
        <LoadingSpinner
          message={status === 'redirecting' ? 'Returning to the connector...' : 'Loading authorization request...'}
        />
      </>
    );
  }

  if (status === 'error' || !details) {
    return (
      <>
        <WebHead><title>Authorize Per Diem Connector</title></WebHead>
        <View className="flex-1 items-center justify-center bg-gray-50 px-6 dark:bg-gray-950">
          <View className="w-full max-w-lg rounded-lg border border-gray-200 bg-white p-6 dark:border-gray-800 dark:bg-gray-900">
            <Text className="text-xl font-semibold text-gray-950 dark:text-white">
              Authorization could not continue
            </Text>
            <Text className="mt-3 text-sm leading-6 text-gray-600 dark:text-gray-300">
              {errorMessage ?? 'This assistant connection request is no longer valid.'}
            </Text>
            <Button
              className="mt-6"
              variant="secondary"
              onPress={() => router.replace('/(auth)/login')}
            >
              Back to sign in
            </Button>
          </View>
        </View>
      </>
    );
  }

  const clientHost = formatClientHost(details.client.uri);
  const redirectHost = formatClientHost(details.redirect_uri);

  return (
    <>
      <WebHead><title>Authorize Per Diem Connector</title></WebHead>
      <ScrollView className="flex-1 bg-gray-50 dark:bg-gray-950">
        <View className="min-h-screen w-full items-center justify-center px-4 py-10">
          <View className="w-full max-w-2xl rounded-lg border border-gray-200 bg-white p-6 shadow-sm dark:border-gray-800 dark:bg-gray-900">
            <View className="flex-row items-center gap-3">
              {details.client.logo_uri ? (
                <Image
                  source={{ uri: details.client.logo_uri }}
                  className="h-11 w-11 rounded-lg bg-gray-100 dark:bg-gray-800"
                  accessibilityIgnoresInvertColors
                />
              ) : (
                <View className="h-11 w-11 items-center justify-center rounded-lg bg-green-100 dark:bg-green-950">
                  <Text className="text-base font-semibold text-green-700 dark:text-green-300">
                    AI
                  </Text>
                </View>
              )}
              <View className="min-w-0 flex-1">
                <Text className="text-xs font-semibold uppercase text-green-700 dark:text-green-400">
                  Per Diem connector
                </Text>
                <Text className="mt-1 text-2xl font-semibold text-gray-950 dark:text-white">
                  Allow {details.client.name} to access Per Diem?
                </Text>
              </View>
            </View>

            <Text className="mt-5 text-base leading-7 text-gray-700 dark:text-gray-200">
              This will let {details.client.name} answer read-only questions using your Per Diem data.
            </Text>

            <View className="mt-6 gap-3">
              {TOOL_ACCESS.map((item) => (
                <View key={item} className="flex-row rounded-lg bg-gray-50 px-4 py-3 dark:bg-gray-800">
                  <Text className="mr-3 text-xs font-semibold uppercase text-green-700 dark:text-green-400">
                    Read
                  </Text>
                  <Text className="flex-1 text-sm leading-5 text-gray-700 dark:text-gray-200">
                    {item}
                  </Text>
                </View>
              ))}
            </View>

            <View className="mt-6 rounded-lg border border-gray-200 p-4 dark:border-gray-800">
              <Text className="text-sm font-semibold text-gray-950 dark:text-white">
                Connection details
              </Text>
              <View className="mt-3 gap-2">
                {clientHost ? (
                  <Text className="text-sm text-gray-600 dark:text-gray-300">
                    Assistant website: {clientHost}
                  </Text>
                ) : null}
                {redirectHost ? (
                  <Text className="text-sm text-gray-600 dark:text-gray-300">
                    Returning to: {redirectHost}
                  </Text>
                ) : null}
                {requestedScopes.length > 0 ? (
                  <Text className="text-sm text-gray-600 dark:text-gray-300">
                    Account details requested: {requestedScopes.map((scope) => SCOPE_COPY[scope] ?? scope).join(', ')}
                  </Text>
                ) : null}
              </View>
            </View>

            <View className="mt-6 flex-col gap-3 sm:flex-row sm:justify-end">
              <Button
                variant="ghost"
                disabled={decisionInFlight !== null}
                loading={decisionInFlight === 'deny'}
                onPress={() => {
                  void completeDecision('deny');
                }}
              >
                Deny
              </Button>
              <Button
                disabled={decisionInFlight !== null}
                loading={decisionInFlight === 'approve'}
                onPress={() => {
                  void completeDecision('approve');
                }}
              >
                Allow access
              </Button>
            </View>
          </View>
        </View>
      </ScrollView>
    </>
  );
}
